import { union } from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Box } from '~/eds/src/components/Box/Box';
import { CheckboxGroup } from '~/eds/src/components/CheckboxGroup/CheckboxGroup';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Text } from '~/eds/src/components/Text/Text';
import { ExportExcelField } from '~/features/advanced-search/types';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { api } from '~/redux/api/createApi';
import { Nullable } from '~/types/Nullable';

import { Fields } from '../Fields/Fields';
import { OptionsManager } from '../OptionsManager/OptionsManager';

type Props = {
  selectedFieldIds?: string[];
  settings: Record<string, Nullable<string[]>>;
  data?: {
    fields: ExportExcelField[];
    clauses: Array<{
      label: string;
      value: string;
    }>;
    tables: Array<{
      label: string;
      value: string;
    }>;
  };
  onChange: (localSettings: Record<string, Nullable<string[]>>) => void;
};

export const useExportSettings = ({
  data,
  selectedFieldIds = [],
  settings,
  onChange,
}: Props) => {
  const intl = useIntl();
  const enableClauseValueExportSettings = useFlag(
    FlagType.ClauseValueExportSettings,
  );
  const [search, setSearch] = useState('');

  const { data: sections } =
    api.endpoints.getFilterSections.useQuery(undefined);

  // add missing table colums as options in the export settings
  const missingFieldIds = useMemo(
    () =>
      selectedFieldIds.filter(
        (fieldId) => !data?.fields.map((field) => field.id).includes(fieldId),
      ),
    [selectedFieldIds, data?.fields],
  );
  const missingFields = useMemo(
    () =>
      missingFieldIds.map((fieldId) => {
        const section = sections?.fieldGroups.find((section) =>
          section.fieldIds.includes(fieldId),
        );
        const field = sections?.fields[fieldId];
        return {
          label: field?.label || '',
          section: section?.label || '',
          id: fieldId,
        };
      }),
    [missingFieldIds, sections?.fieldGroups, sections?.fields],
  );

  const allFields = [...missingFields, ...(data?.fields || [])];

  const fieldOptionIds = useMemo(
    () =>
      (allFields.filter((field) => searchFilter(field, search)) || []).map(
        (field) => `${field.id}`,
      ),
    [allFields, search],
  );

  const clauseOptions = useMemo(
    () => data?.clauses?.filter((clause) => searchFilter(clause, search)) || [],
    [data?.clauses, search],
  );
  const clauseOptionsIds = useMemo(
    () => clauseOptions.map((clause) => clause.value),
    [clauseOptions],
  );

  const tableOptions = useMemo(
    () => data?.tables?.filter((table) => searchFilter(table, search)) || [],
    [data?.tables, search],
  );

  const tableOptionsIds = useMemo(
    () => tableOptions.map((table) => table.value),
    [tableOptions],
  );

  const handleSelectAll = useCallback(
    (value: Nullable<boolean>) => {
      if (value) {
        onChange({
          fields: union(settings.fields, fieldOptionIds),
          clauses: union(settings.clauses, clauseOptionsIds),
          tables: union(settings.tables, tableOptionsIds),
        });
      } else {
        onChange({
          fields:
            settings.fields?.filter(
              (field) => !fieldOptionIds.includes(field),
            ) || [],
          clauses:
            settings.clauses?.filter(
              (clause) => !clauseOptionsIds.includes(clause),
            ) || [],
          tables:
            settings.tables?.filter(
              (table) => !tableOptionsIds.includes(table),
            ) || [],
        });
      }
    },
    [fieldOptionIds, clauseOptionsIds, tableOptionsIds, settings],
  );

  const handleSelectAllOptions = useCallback(
    (
      value: Nullable<boolean>,
      entity: 'fields' | 'clauses' | 'tables',
      options: string[],
    ) => {
      if (value) {
        onChange({
          ...settings,
          [entity]: union(settings[entity], options),
        });
      } else {
        onChange({
          ...settings,
          [entity]:
            settings[entity]?.filter((opt) => !options.includes(opt)) || [],
        });
      }
    },
    [settings],
  );

  const handleSelectAllFields = useCallback(
    (value: Nullable<boolean>) => {
      handleSelectAllOptions(value, 'fields', fieldOptionIds);
    },
    [fieldOptionIds, selectedFieldIds, settings],
  );

  const handleSelectAllClauses = useCallback(
    (value: Nullable<boolean>) => {
      handleSelectAllOptions(value, 'clauses', clauseOptionsIds);
    },
    [clauseOptionsIds, settings],
  );

  const handleSelectAllTables = useCallback(
    (value: Nullable<boolean>) => {
      handleSelectAllOptions(value, 'tables', tableOptionsIds);
    },
    [tableOptionsIds, settings],
  );

  const isAllSelected = (
    options: string[],
    selectedOptions: Nullable<string[]>,
  ) => options.every((opt) => (selectedOptions || []).includes(opt));

  const isAnySelected = (
    options: string[],
    selectedOptions: Nullable<string[]>,
  ) => options.some((field) => (selectedOptions || []).includes(field));

  const fieldsSelectAll = useMemo(() => {
    if (!fieldOptionIds.length) return false;

    const allFieldsSelected = isAllSelected(fieldOptionIds, settings?.fields);
    if (allFieldsSelected) return true;

    return isAnySelected(fieldOptionIds, settings.fields) ? null : false;
  }, [fieldOptionIds, settings?.fields]);

  const clausesSelectAll = useMemo(() => {
    if (!clauseOptionsIds.length) return false;

    const allClauses = isAllSelected(clauseOptionsIds, settings?.clauses);
    if (allClauses) return true;

    return isAnySelected(clauseOptionsIds, settings?.clauses) ? null : false;
  }, [clauseOptionsIds, settings?.clauses]);

  const tablesSelectAll = useMemo(() => {
    if (!tableOptionsIds.length) return false;

    const allTables = isAllSelected(tableOptionsIds, settings?.tables);
    if (allTables) return true;

    return isAnySelected(tableOptionsIds, settings?.tables) ? null : false;
  }, [tableOptionsIds, settings?.tables]);

  const allSelectAll = useMemo(() => {
    const options = {
      fields: fieldOptionIds,
      clauses: clauseOptionsIds,
      tables: tableOptionsIds,
    };

    const allFields = isAllSelected(options.fields, settings?.fields);
    const allClauses = isAllSelected(options.clauses, settings?.clauses);
    const allTables = isAllSelected(options.tables, settings?.tables);

    if (allFields && allClauses && allTables) {
      return true;
    } else {
      const someFields = isAnySelected(options.fields, settings?.fields);
      const someClauses = isAnySelected(options.clauses, settings?.clauses);
      const someTables = isAnySelected(options.tables, settings?.tables);
      return someFields || someClauses || someTables ? null : false;
    }
  }, [settings, fieldOptionIds, clauseOptionsIds, tableOptionsIds]);

  const tabs = [
    {
      value: 'all',
      label: intl.formatMessage({
        id: 'useExportSettings.allTab',
        defaultMessage: 'All',
        description: 'Tab label for all export options',
      }),
      items: data?.fields,
      panel: (
        <Box pr={4} pl={4} pb={4}>
          <OptionsManager
            search={search}
            selectAll={allSelectAll}
            onSearch={setSearch}
            onSelectAllToggle={handleSelectAll}
            options={[
              ...fieldOptionIds,
              ...clauseOptionsIds,
              ...tableOptionsIds,
            ]}
          />
          {!fieldOptionIds.length &&
          !clauseOptions.length &&
          !tableOptions.length ? (
            <NoResults search={search} />
          ) : (
            <>
              {fieldOptionIds?.length ? (
                <Box pr={4} pl={4}>
                  <Box mb={2}>
                    <Text variant="tiny">
                      {intl.formatMessage({
                        id: 'useExportSettings.fieldsSection',
                        defaultMessage: 'Fields',
                        description: 'Section header for fields',
                      })}
                    </Text>
                  </Box>
                  <Fields
                    value={settings.fields}
                    onChange={(fields) => onChange({ ...settings, fields })}
                    fields={allFields}
                    selectedFieldIds={selectedFieldIds}
                    search={search}
                  />
                </Box>
              ) : null}

              {clauseOptions.length ? (
                <Box mt={4} pr={4} pl={4}>
                  <Box mb={2}>
                    <Text variant="tiny">
                      {intl.formatMessage({
                        id: 'useExportSettings.clausesSection',
                        defaultMessage: 'Clauses',
                        description: 'Section header for clauses',
                      })}
                    </Text>
                  </Box>

                  <CheckboxGroup
                    name="Clauses"
                    onChange={(clauses) => onChange({ ...settings, clauses })}
                    options={clauseOptions || []}
                    value={settings.clauses}
                    columns={3}
                  />
                </Box>
              ) : null}

              {tableOptions.length ? (
                <Box mt={4} pr={4} pl={4}>
                  <Box mb={2}>
                    <Text variant="tiny">
                      {intl.formatMessage({
                        id: 'useExportSettings.tablesSection',
                        defaultMessage: 'Tables',
                        description: 'Section header for tables',
                      })}
                    </Text>
                  </Box>

                  <CheckboxGroup
                    name="Tables"
                    onChange={(tables) => onChange({ ...settings, tables })}
                    options={tableOptions || []}
                    value={settings.tables}
                    columns={3}
                  />
                </Box>
              ) : null}
            </>
          )}
        </Box>
      ),
    },
    {
      value: 'fields',
      label: intl.formatMessage({
        id: 'useExportSettings.fieldsTab',
        defaultMessage: 'Fields',
        description: 'Tab label for fields',
      }),
      items: data?.fields,
      panel: (
        <Box p={4} flex="1">
          <OptionsManager
            search={search}
            selectAll={fieldsSelectAll}
            onSearch={setSearch}
            onSelectAllToggle={handleSelectAllFields}
            options={fieldOptionIds}
          />
          {!fieldOptionIds.length ? (
            <NoResults search={search} />
          ) : (
            <Fields
              selectedFieldIds={selectedFieldIds}
              value={settings.fields}
              onChange={(fields) => onChange({ ...settings, fields })}
              fields={allFields}
              search={search}
            />
          )}
        </Box>
      ),
    },
    {
      value: 'clauses',
      label: intl.formatMessage({
        id: 'useExportSettings.clausesTab',
        defaultMessage: 'Clauses',
        description: 'Tab label for clauses',
      }),
      items: data?.clauses,
      panel: (
        <Box pr={4} pl={4} flex="1">
          <OptionsManager
            search={search}
            selectAll={clausesSelectAll}
            onSearch={setSearch}
            onSelectAllToggle={handleSelectAllClauses}
            options={clauseOptionsIds}
          />
          {!clauseOptions.length ? (
            <NoResults search={search} />
          ) : (
            <CheckboxGroup
              name="Clauses"
              onChange={(clauses) => onChange({ ...settings, clauses })}
              options={clauseOptions}
              value={settings.clauses}
              columns={3}
            />
          )}
        </Box>
      ),
    },
  ];

  if (!enableClauseValueExportSettings) {
    tabs.push({
      value: 'tables',
      label: intl.formatMessage({
        id: 'useExportSettings.tablesTab',
        defaultMessage: 'Tables',
        description: 'Tab label for tables',
      }),
      items: data?.tables,
      panel: (
        <Box pr={4} pl={4} flex="1">
          <OptionsManager
            search={search}
            selectAll={tablesSelectAll}
            onSearch={setSearch}
            onSelectAllToggle={handleSelectAllTables}
            options={tableOptionsIds}
          />
          {!tableOptions.length ? (
            <NoResults search={search} />
          ) : (
            <CheckboxGroup
              name="Tables"
              onChange={(tables) => onChange({ ...settings, tables })}
              options={tableOptions}
              value={settings.tables}
              columns={3}
            />
          )}
        </Box>
      ),
    });
  }

  return tabs;
};

const NoResults = ({ search }: { search: string }) => {
  return (
    <Layout align="center" justify="center">
      <Text>
        <FormattedMessage
          id="useExportSettings.noResults"
          defaultMessage="No results for: <strong>{search}</strong>"
          description="Message when no search results are found. Example: search = 'contract terms'"
          values={{
            search,
            strong: (chunks) => <strong>{chunks}</strong>,
          }}
        />
      </Text>
    </Layout>
  );
};

const searchFilter = (
  filter: ExportExcelField | { label: string },
  search: string,
) => filter.label?.toLowerCase().indexOf(search.toLowerCase()) >= 0;
